import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { getCreditsApi } from '../../../services/api';
import useSnackMessage from '../../../hooks/useSnackMessage';

const GENERIC_FAILURE = { http_code: 500, message: 'Something went wrong. Please try again.' };

const fetchCredits = async () => {
  const res = await getCreditsApi();
  return res?.data;
};

// Loads the owner's credits payload and runs writes against it. Every write
// toasts the server's envelope and re-fetches; only the row whose key is in
// `pending` is disabled while its request runs.
export default function useCredits() {
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const token = window.localStorage.getItem('token');
  const [showSnackbar] = useSnackMessage();
  const [pending, setPending] = useState({});

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery(
    ['credits', commonReducer.user_id],
    fetchCredits,
    {
      enabled: !!token && !!commonReducer.is_account_owner,
      retry: false,
      staleTime: 0,
      cacheTime: 0,
      onError: (err) => {
        // 401 logs out through the toast hook; other errors render inline.
        if (err?.response?.status === 401) showSnackbar(err.response.data);
      },
    }
  );

  const run = useCallback(async (key, fn) => {
    setPending((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await fn();
      showSnackbar(res?.data || GENERIC_FAILURE);
    } catch (err) {
      showSnackbar(err?.response?.data || GENERIC_FAILURE);
    } finally {
      setPending((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      refetch();
    }
  }, [refetch, showSnackbar]);

  return {
    data: data || null,
    isLoading,
    isFetching,
    isError,
    errorMessage: error?.response?.data?.message || (isError ? GENERIC_FAILURE.message : null),
    refetch,
    run,
    pending,
  };
}
